import { ColumnConfiguration, GridData, ValueOption } from 'api';
import { v4 as uuidv4 } from 'uuid';

export type ExternalProviderMapping = {
  npi: string;
  lastName: string;
  firstName: string;
  specialty: string;
  orgName: string;
  groupPacId: string;
  startDate: Date;
  endDate: Date;
  normalizedOrganizationName?: string;
  normalizedOrganizationType?: string;
  normalizedOrganizationTypeDetail?: string;
  primaryGroupFlag: boolean;
};

export type ExternalProviderMappingUpdateDto = {
  npi: string;
  groupPacId: string;
  startDate: Date;
  endDate: Date;
  normalizedOrganizationName?: string;
  normalizedOrganizationType?: ValueOption;
  normalizedOrganizationTypeDetail?: string;
};

export type ExternalProviderMappingResponse = {
  externalProviderMappings: ExternalProviderMapping[];
  normalizedOrganizationTypes: ValueOption[];
  validationError?: string;
};

export type ExternalProviderSearchState = {
  npiSearchValue: string | number;
  orgNameSearchValue: string;
  validationErrors: string[];
};

export type ExternalProviderSearchRequest = {
  npi?: string | number;
  organizationName?: string;
};

//TODO: Return columns from API endpoint
const externalPoviderMappingColumns: ColumnConfiguration[] = [
  {
    name: 'npi',
    displayName: 'NPI',
    type: 'string',
    required: false,
    defaultValue: '',
    editable: false,
    width: 120,
  },
  {
    name: 'firstName',
    displayName: 'First Name',
    type: 'string',
    required: false,
    defaultValue: '',
    editable: false,
    width: 120,
  },
  {
    name: 'lastName',
    displayName: 'Last Name',
    type: 'string',
    required: false,
    defaultValue: '',
    editable: false,
    width: 120,
  },
  {
    name: 'specialty',
    displayName: 'Specialty',
    type: 'string',
    required: false,
    defaultValue: '',
    editable: false,
    width: 200,
  },
  {
    name: 'orgName',
    displayName: 'Org Name',
    type: 'string',
    required: false,
    defaultValue: '',
    editable: false,
    width: 200,
  },
  {
    name: 'groupPacId',
    displayName: 'PAC ID',
    type: 'string',
    required: false,
    defaultValue: '',
    editable: false,
    width: 120,
  },
  {
    name: 'startDate',
    displayName: 'Start Date',
    type: 'date',
    required: false,
    defaultValue: '',
    editable: false,
    width: 120,
  },
  {
    name: 'endDate',
    displayName: 'End Date',
    type: 'date',
    required: false,
    defaultValue: '',
    editable: false,
    width: 120,
  },
  {
    name: 'normalizedOrganizationName',
    displayName: 'Normalized Org Name',
    type: 'string',
    required: true,
    defaultValue: '',
    editable: true,
    width: 160,
  },
  {
    name: 'normalizedOrganizationType',
    displayName: 'Normalized Org Type',
    type: 'singleSelect',
    required: true,
    defaultValue: { value: 'None', label: 'None' },
    editable: true,
    width: 160,
  },
  {
    name: 'normalizedOrganizationTypeDetail',
    displayName: 'Normalized Org Type Detail',
    type: 'string',
    required: true,
    defaultValue: '',
    editable: true,
    width: 220,
  },
  {
    name: 'actions',
    type: 'actions',
    displayName: 'Actions',
    width: 100,
    cellClassName: 'actions',
    required: true,
    editable: false,
    defaultValue: '',
  },
];

export const convertExternalProviderMappingsToDataGrid = (
  externalProviderMappingResponse: ExternalProviderMappingResponse
): GridData => {
  const rows = externalProviderMappingResponse.externalProviderMappings?.map(
    (externalProviderMapping: any) => ({
      ...externalProviderMapping,
      id: uuidv4(),
      isNew: false,
    })
  );

  const columns = externalPoviderMappingColumns.map(column => {
    if (column.name === 'normalizedOrganizationType')
      return {
        ...column,
        valueOptions:
          externalProviderMappingResponse.normalizedOrganizationTypes?.map(
            (orgType, i) => ({ ...orgType, id: i })
          ),
      };

    return column;
  });

  const initializeRowsWithValueOptions = rows?.map(
    (row: ExternalProviderMapping) => {
      const found =
        externalProviderMappingResponse.normalizedOrganizationTypes.find(
          x => x.value === row.normalizedOrganizationType
        );

      return {
        ...row,
        normalizedOrganizationType: found ? found : { value: '', label: '' },
      };
    }
  );

  return {
    columns: columns,
    rows: initializeRowsWithValueOptions,
    validationError: externalProviderMappingResponse?.validationError,
  };
};
